import type { ReactNode } from "react";

import { cx } from "../lib/cx";
import type { OutlineEntry } from "./outline";

/**
 * The outline panel (`docs/spec/editor.md` §13). Its side is the workspace's
 * `tocPosition` preference; `Ctrl+Alt+H` opens and closes it.
 */
export function TableOfContentsPanel({
  entries,
  position,
  onClose,
}: {
  entries: OutlineEntry[];
  position: "left" | "right";
  onClose: () => void;
}): ReactNode {
  return (
    <aside
      aria-label="Table of contents"
      className={cx(
        "w-56 shrink-0 border-line text-sm",
        position === "left" ? "order-first border-r pr-4" : "order-last border-l pl-4",
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold tracking-wide text-muted uppercase">Contents</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Hide table of contents"
          className="rounded px-1 text-xs text-muted hover:bg-hover hover:text-ink"
        >
          ×
        </button>
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-muted">This document has no headings.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {entries.map((entry) => (
            <li key={entry.id} style={{ paddingLeft: `${String(entry.depth * 12)}px` }}>
              <a href={`#${entry.id}`} className="text-muted hover:text-ink hover:underline">
                {entry.text}
              </a>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
